import React, {
    createContext,
    type FC,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, notify, Thumbnail, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconClear } from '@bynder/icons';
import exportService from 'packages/services/exportService';
import { useSocketSubscribe } from 'packages/socket';
import { InProgressJobData, subscribeByJobIdExportJobsOptions } from 'packages/socket/subscribeOptions/exportJobs';
import filtersStorage from 'packages/hooks/filtersStorage';
import { ExportPresetValue } from 'packages/hooks/useExportPreset';
import { download, downloadPdf } from 'packages/pages/activity/utils';
import { CloseButtonContainer, ContentWrapper, Tooltip } from './FlyInnotifications.styled';

type ExportProgressNotificationProps = {
    show: boolean;
    notificationId: number;
    exportType: ExportPresetValue;
    creativeName: string;
    thumbnailUrl: string;
};

type ExportStatus = 'PREPARING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

interface FlyInNotificationContextType {
    showNotification: (props: ExportProgressNotificationProps) => void;
    hideNotification: (notificationId: string) => void;
}

const FlyInNotificationContext = createContext<FlyInNotificationContextType>({
    showNotification: () => {},
    hideNotification: () => {},
});

const ExportJobSubscription = ({
    jobId,
    notificationId,
    notificationDetails,
    onMessage,
}: {
    jobId: number;
    notificationId: string;
    notificationDetails: ExportProgressNotificationProps;
    onMessage: (data: InProgressJobData, notificationDetails: ExportProgressNotificationProps) => void;
}) => {
    const [subscribed, setSubscribed] = useState(false);

    const handleMessage = useCallback(
        (data: InProgressJobData) => {
            onMessage(data, notificationDetails);
        },
        [onMessage, notificationDetails],
    );

    const subscribe = useSocketSubscribe(
        subscribeByJobIdExportJobsOptions({
            jobId,
            onMessage: handleMessage,
        }),
    );

    useEffect(() => {
        if (!subscribed) {
            subscribe();
            setSubscribed(true);
        }
    }, [subscribe, subscribed, notificationId, notificationDetails]);

    return null;
};

export const FlyInNotificationProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { translate } = useTranslate();
    const navigate = useNavigate();
    const [activeNotifications, setActiveNotifications] = useState<Record<string, ExportProgressNotificationProps>>({});
    const [hiddenNotifications, setHiddenNotifications] = useState<Set<string>>(new Set());
    const { clearFiltersFromStorage } = filtersStorage();

    const getContent = (text: string) => {
        return `‘${text.length > 48 ? `${text.substring(0, 48)}...` : text}’`;
    };

    const getNotificationTitle = useMemo(
        () => (exportType: ExportPresetValue, creativeName: string) => {
            return `${
                exportType === ExportPresetValue.BYNDER
                    ? translate('pages.activity.item.details.header.waiting-room')
                    : translate('pages.activity.item.details.header.download')
            } ${getContent(creativeName)}`;
        },
        [],
    );

    const getDescription = (status: ExportStatus, exportCount: number, totalItems: number) => {
        switch (status) {
            case 'PREPARING': {
                return translate('notification.quick-export.preparing');
            }
            case 'IN_PROGRESS': {
                return translate('notification.quick-export.exporting-count', {
                    count: exportCount,
                    total: totalItems,
                });
            }
            case 'FAILED': {
                return translate('notification.quick-export.failed');
            }

            default: {
                return translate('notification.quick-export.completed');
            }
        }
    };

    const getNotificationProps = ({
        notificationId,
        exportType,
        creativeName,
        status,
        thumbnailUrl,
        exportCount,
        totalItems,
    }: {
        notificationId: string;
        exportType: ExportPresetValue;
        creativeName: string;
        status: ExportStatus;
        thumbnailUrl: string;
        exportCount: number;
        totalItems: number;
    }) => ({
        isPersistent: true,
        id: notificationId,
        title: <ContentWrapper>{getNotificationTitle(exportType, creativeName)}</ContentWrapper>,
        description: getDescription(status, exportCount, totalItems),
        progressPercentage: 0,
        thumbnail: (
            <Thumbnail variant="content" imageUrl={thumbnailUrl}>
                {['COMPLETED', 'FAILED'].includes(status) ? (
                    <Thumbnail.Indicator type={status === 'COMPLETED' ? 'positive' : 'error'} />
                ) : null}
            </Thumbnail>
        ),
        button: (
            <CloseButtonContainer onClick={() => hideNotification(notificationId.toString())}>
                <IconClear style={{ color: token.gray200 }} />
                <Tooltip>{translate('notification.quick-export.hide')}</Tooltip>
            </CloseButtonContainer>
        ),
    });

    const getProgressPercentage = (exportItems) =>
        Math.round(
            exportItems.reduce((accItems, item) => {
                const { progresses } = item;
                const progress =
                    progresses.reduce((acc, { progress }) => {
                        return acc + progress;
                    }, 0) / progresses.length;

                return accItems + progress;
            }, 0),
        );

    const getExportCount = (totalItems, exportItems) =>
        Math.round((totalItems * getProgressPercentage(exportItems)) / 100) === 0
            ? 1
            : Math.round((totalItems * getProgressPercentage(exportItems)) / 100);

    const getNotificationButtons = (notificationId) => (
        <Flex direction="row" alignItems="center">
            <Button variant="clean" onClick={() => onViewActivity(notificationId)}>
                {translate('notification.quick-export.view')}
            </Button>
            <CloseButtonContainer onClick={() => hideNotification(notificationId.toString())}>
                <IconClear style={{ color: token.gray200 }} />
                <Tooltip>{translate('notification.quick-export.hide')}</Tooltip>
            </CloseButtonContainer>
        </Flex>
    );

    const downloadExportItem = (exportJobId: number, exportType: ExportPresetValue) => {
        if (exportType !== ExportPresetValue.DOWNLOAD) {
            return;
        }

        exportService
            .getActivityFeedItems({
                exportJobId,
            })
            .then((data: any) => {
                const { items } = data.json;
                const isPdfExport = items[0].renditionConfiguration.type === 'PDF';

                if ((items.length === 1 && items[0].jobVariations.length === 1) || isPdfExport) {
                    if (isPdfExport) {
                        downloadPdf(items[0].id);
                    } else {
                        download(exportJobId, items[0].jobVariations[0].id);
                    }
                } else {
                    download(exportJobId);
                }
            });
    };

    const onMessage = useCallback(
        (data: InProgressJobData, notificationDetails: ExportProgressNotificationProps) => {
            const { notificationId, exportType, creativeName, thumbnailUrl } = notificationDetails;
            const exportItems = data.items;

            const hasItems = exportItems.length > 0;
            const totalItems = hasItems ? data.items[0].totalVariationsCount : 1;
            const exportCount = getExportCount(totalItems, exportItems);

            let status: ExportStatus = 'PREPARING';

            if (
                hasItems &&
                exportItems.every((item) =>
                    item.jobVariations.every((variation) => ['UPLOADING_FAILED', 'FAILED'].includes(variation.status)),
                )
            ) {
                status = 'FAILED';
            }

            if (
                (hasItems &&
                    exportItems.every((item) =>
                        item.jobVariations.every((variation) => ['COMPLETED'].includes(variation.status)),
                    )) ||
                !hasItems
            ) {
                status = 'COMPLETED';
            }

            if (
                hasItems &&
                exportItems.every((item) =>
                    item.jobVariations.every((variation) => ['UPLOADING', 'CREATING'].includes(variation.status)),
                )
            ) {
                status = 'IN_PROGRESS';
            }

            const notificationProps = getNotificationProps({
                notificationId: notificationId.toString(),
                exportType,
                creativeName,
                status,
                thumbnailUrl,
                exportCount,
                totalItems,
            });

            if (!data.items.length || status === 'FAILED') {
                if (status === 'FAILED') {
                    notify({
                        ...notificationProps,
                        variant: 'error',
                        button: getNotificationButtons(notificationId),
                    });
                } else {
                    notify({
                        ...notificationProps,
                        variant: 'success',
                        button: getNotificationButtons(notificationId),
                    });

                    downloadExportItem(notificationId, exportType);
                }

                setActiveNotifications((prev) => {
                    const newState = { ...prev };
                    delete newState[notificationId];

                    return newState;
                });
            } else if (status === 'COMPLETED') {
                notify({
                    ...notificationProps,
                    variant: 'success',
                    button: getNotificationButtons(notificationId),
                });

                downloadExportItem(notificationId, exportType);
            } else if (!hiddenNotifications.has(notificationId.toString())) {
                notify({
                    ...notificationProps,
                    variant: 'loading',
                    progressPercentage: status === 'IN_PROGRESS' ? getProgressPercentage(exportItems) : undefined,
                });
            }
        },
        [translate, navigate, hiddenNotifications],
    );

    const hideNotification = useCallback((notificationId: string) => {
        notify.remove(notificationId);
        setHiddenNotifications((prev) => new Set(prev).add(notificationId));
    }, []);

    const onViewActivity = useCallback((notificationId) => {
        clearFiltersFromStorage('activity');
        navigate('/activity');
        notify.remove(notificationId);
    }, []);

    const showNotification = useCallback((props: ExportProgressNotificationProps) => {
        if (!props.show) {
            return;
        }

        setActiveNotifications((prev) => ({
            ...prev,
            [props.notificationId]: props,
        }));
    }, []);

    return (
        <FlyInNotificationContext.Provider value={{ showNotification, hideNotification }}>
            {children}
            {Object.entries(activeNotifications).map(([notificationId, details]) => (
                <ExportJobSubscription
                    key={notificationId}
                    jobId={Number(notificationId)}
                    notificationId={notificationId}
                    notificationDetails={details}
                    onMessage={onMessage}
                />
            ))}
        </FlyInNotificationContext.Provider>
    );
};

export const useFlyInNotificationContext = () => useContext(FlyInNotificationContext);
